// 2026-06-30 - FormuLearning - 관리자: 호출 목록/프로젝트 타임라인 (REQ-FN-ADMIN-03)
// 파일 위치: frontend/src/features/admin/CallLogList.tsx
// 필터(API·사용자·프로젝트·성공여부)+키셋 페이징. 헤더 클릭 정렬(로드된 행 대상).
// 행 클릭 시 단건 상세(CallDetailModal).

import { useEffect, useMemo, useState } from 'react'
import { listCalls, listIntegrations, listAdminUsers, listAdminProjects } from '../../api/admin'
import type { ApiIntegration, AdminUserRef, AdminProjectRef, CallLog } from '../../types/admin'
import { opLabel } from '../../types/admin'
import CallDetailModal from './CallDetailModal'

type SortKey = 'called_at' | 'api_code' | 'user_name' | 'project_name' | 'step' | 'substep' | 'success' | 'duration_ms' | 'request_bytes' | 'response_bytes'
type SortDir = 'asc' | 'desc'

const COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'called_at', label: 'Time' },
  { key: 'api_code', label: 'API' },
  { key: 'user_name', label: 'User' },
  { key: 'project_name', label: 'Project' },
  { key: 'step', label: 'Step' },
  { key: 'substep', label: 'Substep' },
  { key: 'success', label: 'Status' },
  { key: 'duration_ms', label: 'ms' },
  { key: 'request_bytes', label: 'Sent' },
  { key: 'response_bytes', label: 'Recv' },
]

function fmtBytes(n: number | null): string {
  if (n == null) return '—'
  if (n < 1024) return `${n} B`
  return `${(n / 1024).toFixed(1)} KB`
}

function compare(a: CallLog, b: CallLog, key: SortKey): number {
  const va = a[key]
  const vb = b[key]
  if (va == null && vb == null) return 0
  if (va == null) return 1 // null은 뒤로
  if (vb == null) return -1
  if (typeof va === 'number' && typeof vb === 'number') return va - vb
  if (typeof va === 'boolean' && typeof vb === 'boolean') return va === vb ? 0 : va ? 1 : -1
  return String(va).toLowerCase().localeCompare(String(vb).toLowerCase())
}

export default function CallLogList() {
  const [integrations, setIntegrations] = useState<ApiIntegration[]>([])
  const [users, setUsers] = useState<AdminUserRef[]>([])
  const [projects, setProjects] = useState<AdminProjectRef[]>([])
  const [apiId, setApiId] = useState('')
  const [userId, setUserId] = useState('')
  const [projectId, setProjectId] = useState('')
  const [success, setSuccess] = useState<'' | 'true' | 'false'>('')
  const [groupId, setGroupId] = useState('')  // 오퍼레이션(request_group) 필터 — 상세에서 진입
  const [rows, setRows] = useState<CallLog[]>([])
  const [cursor, setCursor] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir } | null>(null)

  useEffect(() => {
    listIntegrations().then(setIntegrations).catch(() => {})
    listAdminUsers().then(setUsers).catch(() => {})
    listAdminProjects().then(setProjects).catch(() => {})
  }, [])

  async function load(reset: boolean) {
    setLoading(true)
    try {
      const page = await listCalls({
        api_id: apiId || undefined,
        user_id: userId || undefined,
        project_id: projectId || undefined,
        request_group_id: groupId || undefined,
        success: success === '' ? undefined : success === 'true',
        cursor: reset ? undefined : cursor ?? undefined,
        limit: 50,
      })
      setRows((prev) => (reset ? page.items : [...prev, ...page.items]))
      setCursor(page.next_cursor)
    } finally {
      setLoading(false)
    }
  }

  // 필터 변경 시 처음부터 다시 조회
  useEffect(() => { load(true) /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [apiId, userId, projectId, success, groupId])

  function toggleSort(key: SortKey) {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' }
      if (prev.dir === 'asc') return { key, dir: 'desc' }
      return null // desc → 정렬 해제(기본 seq 내림차순 순서)
    })
  }

  const sortedRows = useMemo(() => {
    if (!sort) return rows
    const sorted = [...rows].sort((a, b) => compare(a, b, sort.key))
    return sort.dir === 'desc' ? sorted.reverse() : sorted
  }, [rows, sort])

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-text-secondary">
          API
          <select value={apiId} onChange={(e) => setApiId(e.target.value)} className="rounded border bg-bg-input px-2 py-1 text-sm text-text-body" style={{ borderColor: 'var(--border-divider)' }}>
            <option value="">All</option>
            {integrations.map((i) => <option key={i.id} value={i.id}>{i.code}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-text-secondary">
          User
          <select value={userId} onChange={(e) => setUserId(e.target.value)} className="rounded border bg-bg-input px-2 py-1 text-sm text-text-body" style={{ borderColor: 'var(--border-divider)' }}>
            <option value="">All</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-text-secondary">
          Project
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="max-w-[220px] rounded border bg-bg-input px-2 py-1 text-sm text-text-body" style={{ borderColor: 'var(--border-divider)' }}>
            <option value="">All</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-text-secondary">
          Success
          <select value={success} onChange={(e) => setSuccess(e.target.value as '' | 'true' | 'false')} className="rounded border bg-bg-input px-2 py-1 text-sm text-text-body" style={{ borderColor: 'var(--border-divider)' }}>
            <option value="">All</option>
            <option value="true">OK</option>
            <option value="false">Fail</option>
          </select>
        </label>
        {(apiId || userId || projectId || success || groupId) && (
          <button
            onClick={() => { setApiId(''); setUserId(''); setProjectId(''); setSuccess(''); setGroupId('') }}
            className="rounded px-3 py-1 text-xs text-text-secondary hover:bg-bg-input hover:text-text-body"
            style={{ border: '1px solid var(--border-divider)' }}
          >
            Reset filters
          </button>
        )}
      </div>

      {groupId && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-text-secondary">Operation (one user action):</span>
          <span className="rounded px-2 py-0.5" style={{ backgroundColor: 'var(--bg-chip-teal)', color: 'var(--color-accent-teal)' }}>
            {opLabel(rows[0]?.operation ?? null)} <span className="font-mono opacity-70">{groupId.slice(0, 8)}</span>
          </span>
          <button onClick={() => setGroupId('')} className="text-text-secondary hover:text-text-body">✕ clear</button>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--border-divider)' }}>
        <table className="w-full text-left text-sm">
          <thead className="text-xs text-text-secondary" style={{ backgroundColor: 'var(--color-bg-panel)' }}>
            <tr>
              {COLUMNS.map((c) => (
                <th
                  key={c.key}
                  onClick={() => toggleSort(c.key)}
                  className="cursor-pointer select-none px-3 py-2 font-medium hover:text-text-body"
                  title="Click to sort"
                >
                  {c.label}
                  {sort?.key === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
              <th className="px-3 py-2 font-medium" title="Operation (one user action). Click to see all its calls.">Operation</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r) => (
              <tr
                key={r.id}
                onClick={() => setDetailId(r.id)}
                className="cursor-pointer border-t hover:bg-bg-input"
                style={{ borderColor: 'var(--border-divider)' }}
              >
                <td className="px-3 py-2 text-text-secondary whitespace-nowrap">{new Date(r.called_at).toLocaleString()}</td>
                <td className="px-3 py-2 font-mono text-xs text-text-primary">{r.api_code}</td>
                <td className="px-3 py-2 text-text-body" title={r.user_email ?? ''}>{r.user_name ?? '—'}</td>
                <td className="px-3 py-2 text-text-body max-w-[200px] truncate" title={r.project_name ?? ''}>{r.project_name ?? '—'}</td>
                <td className="px-3 py-2 text-text-secondary">{r.step ?? '—'}</td>
                <td className="px-3 py-2 text-text-body">{r.substep ?? '—'}</td>
                <td className="px-3 py-2" style={{ color: r.success ? 'var(--color-accent-teal)' : 'var(--color-accent-purple)' }}>
                  {r.success ? 'OK' : 'FAIL'} {r.response_status ?? ''}
                </td>
                <td className="px-3 py-2 text-text-secondary">{r.duration_ms ?? '—'}</td>
                <td className="px-3 py-2 text-text-secondary whitespace-nowrap">{fmtBytes(r.request_bytes)}</td>
                <td className="px-3 py-2 whitespace-nowrap" style={{ color: r.truncated ? 'var(--color-accent-purple)' : undefined }} title={r.truncated ? 'response truncated >64KB (stored)' : ''}>
                  <span className={r.truncated ? '' : 'text-text-secondary'}>{fmtBytes(r.response_bytes)}{r.truncated ? '*' : ''}</span>
                </td>
                <td className="px-3 py-2 text-xs">
                  {r.request_group_id ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); setGroupId(r.request_group_id!) }}
                      title={`Operation run ${r.request_group_id} — click to show all its calls`}
                      className="whitespace-nowrap underline decoration-dotted underline-offset-2"
                      style={{ color: 'var(--color-accent-teal)' }}
                    >
                      {opLabel(r.operation)}
                    </button>
                  ) : <span className="text-text-secondary">—</span>}
                </td>
              </tr>
            ))}
            {sortedRows.length === 0 && !loading && (
              <tr><td colSpan={11} className="px-3 py-6 text-center text-sm text-text-secondary">No calls match the filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3">
        {cursor != null && (
          <button onClick={() => load(false)} disabled={loading} className="rounded px-3 py-1.5 text-xs text-text-body hover:bg-bg-input" style={{ border: '1px solid var(--border-divider)' }}>
            {loading ? 'Loading…' : 'Load more'}
          </button>
        )}
        <span className="text-xs text-text-secondary">
          {rows.length} rows{sort ? ` · sorted by ${sort.key} ${sort.dir}` : ''}
        </span>
      </div>

      <CallDetailModal
        callId={detailId}
        onClose={() => setDetailId(null)}
        onOpenGroup={(gid) => { setGroupId(gid); setDetailId(null) }}
      />
    </div>
  )
}
